import React, { useState } from "react";
import { CHART_1, CHART_2, CHART_3, INK, MUTED, GRID, PAPER } from "./palette";

// ---- group_ellipses: two outcomes against each other by group (MANOVA's H and E in one picture) ----
//
// Props (ChartData#group_ellipses): groups [{ key, name, n, points [[x, y]], mean [x, y], sd [x, y], r, inside }],
// grand [x, y], pooledR, level (.68), radius (the Mahalanobis radius for that level), xLabel, yLabel,
// legendTitle, noun. Each group's ellipse is drawn from its own means, SDs and correlation.
//
// The small helpers below mirror ConceptChart.jsx's (same tokens, axis, legend and tooltip) so this
// file stands alone; keep them in step with it.

const COLORS = [CHART_1, CHART_2, CHART_3];

const W = 640;
const M = { top: 16, right: 16, left: 16 };

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
const fmtR = (x, places = 2) => fmt(x, places).replace(/^(−?)0\./, "$1.");
const minus = (t) => (t < 0 ? `−${-t}` : t);
const scale = (ax, a, b) => (v) => a + ((v - ax.min) / (ax.max - ax.min)) * (b - a);
const lowerWords = (s) => s.split(" ").map((w) => (/^[A-Z][a-z]+$/.test(w) ? w.toLowerCase() : w)).join(" ");
// "Loneliness (20–80)" → "loneliness", for use inside a sentence.
const bare = (s) => lowerWords(s.replace(/\s*\([^)]*\)\s*$/, ""));
const pair = ([a, b]) => `(${fmt(a)}, ${fmt(b)})`;
const article = (w) => `${/^[aeiou]/i.test(w) ? "an" : "a"} ${w}`;
const listing = (parts) => (parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`);

function axis(xs, pad = 0.05) {
	const lo = Math.min(...xs), hi = Math.max(...xs), span = hi - lo || 1;
	const rough = span / 6, mag = 10 ** Math.floor(Math.log10(rough));
	const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rough);
	const min = Math.max(lo >= 0 ? 0 : -Infinity, Math.floor((lo - span * pad) / step) * step), max = Math.ceil((hi + span * pad) / step) * step;
	const ticks = [];
	for (let t = min; t <= max + step / 2; t += step) ticks.push(+t.toFixed(10));
	return { min, max, ticks };
}

function spread(items, gap = 14) {
	const sorted = [...items].sort((a, b) => a.y - b.y);
	sorted.forEach((it, i) => { if (i > 0 && it.y - sorted[i - 1].y < gap) it.y = sorted[i - 1].y + gap; });
	return items;
}

const Cross = ({ cx, cy, size = 6, color = INK, width = 2 }) => (
	<g stroke={color} strokeWidth={width} strokeLinecap="round">
		<line x1={cx - size} x2={cx + size} y1={cy - size} y2={cy + size} />
		<line x1={cx - size} x2={cx + size} y1={cy + size} y2={cy - size} />
	</g>
);

function Legend({ items, title }) {
	const swatch = (kind, color) => {
		switch (kind) {
			case "line": return <span className="inline-block h-0.5 w-4 rounded" style={{ background: color }} />;
			case "dash": return <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: color }} />;
			case "ring": return <span className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px]" style={{ borderColor: INK }} />;
			case "ellipse": return <span className="inline-block h-2.5 w-4 rounded-[50%] border-[1.5px]" style={{ borderColor: color }} />;
			case "cross": return <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><Cross cx={5} cy={5} size={3.5} width={1.75} /></svg>;
			default: return <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />;
		}
	};
	return (
		<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
			{title && <span className="font-semibold text-gray-700">{title}</span>}
			{items.map(({ label, color, kind }) => (
				<span key={label} className="flex items-center gap-1.5">
					{swatch(kind, color)}
					{label}
				</span>
			))}
		</div>
	);
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, M.left), W - M.right - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${M.top})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

// Points on the ellipse { (x, y) : squared Mahalanobis distance = c² } for means m, SDs s and correlation r,
// through the Cholesky factor of the covariance matrix.
function ellipsePoints({ mean: [mx, my], sd: [sx, sy], r }, c, steps = 96) {
	const k = Math.sqrt(Math.max(0, 1 - r * r));
	return Array.from({ length: steps + 1 }, (_, i) => {
		const t = (2 * Math.PI * i) / steps, u = Math.cos(t), v = Math.sin(t);
		return [mx + c * sx * u, my + c * sy * (r * u + k * v)];
	});
}

export default function GroupEllipsesChart({ groups, grand, pooledR, level = 0.68, radius, xLabel, yLabel, legendTitle, noun = "participant" }) {
	const [hover, setHover] = useState(null);
	const height = 360, box = { l: 44, r: W - 150, t: M.top + 18, b: height - 40 };
	const ellipses = groups.map((g) => ellipsePoints(g, radius));
	const axX = axis([...groups.flatMap((g) => g.points.map((p) => p[0])), ...ellipses.flat().map((p) => p[0])], 0.03);
	const axY = axis([...groups.flatMap((g) => g.points.map((p) => p[1])), ...ellipses.flat().map((p) => p[1])], 0.03);
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const path = (pts) => pts.map(([a, b], i) => `${i ? "L" : "M"}${x(a).toFixed(1)},${y(b).toFixed(1)}`).join(" ") + " Z";
	const total = groups.reduce((a, g) => a + g.n, 0);
	const pct = Math.round(level * 100);
	const xl = bare(xLabel), yl = bare(yLabel), gl = lowerWords(legendTitle || "group");
	const labels = spread(groups.map((g, i) => ({ i, y: y(g.mean[1]) + 4 })), 30);
	const dim = (i) => (hover && hover.i !== undefined && hover.i !== i ? 0.25 : 1);
	const tilt = pooledR < 0 ? "downward" : "upward";
	return (
		<figure>
			<Legend title={legendTitle} items={[
				...groups.map((g, i) => ({ label: g.name, color: COLORS[i % 3] })),
				{ label: "Group mean pair (centroid)", kind: "ring" },
				{ label: `${pct}% data ellipse`, color: MUTED, kind: "ellipse" },
				{ label: "Grand mean", kind: "cross" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${yl[0].toUpperCase()}${yl.slice(1)} against ${xl} for ${total} ${noun}s, by ${gl}. Mean pairs (${xl}, ${yl}): ${groups.map((g) => `${g.name} ${pair(g.mean)}`).join(", ")}; grand mean ${pair(grand)}. Each ${gl}'s ${pct}% ellipse tilts ${tilt}: within the groups the two correlate r = ${fmtR(pooledR)}.`}>
				{axY.ticks.map((t) => (
					<g key={`y${t}`}>
						<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{minus(t)}</text>
					</g>
				))}
				{axX.ticks.map((t) => <text key={`x${t}`} x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{minus(t)}</text>)}
				<text x={box.r} y={box.b + 32} fontSize="11" fill={MUTED} textAnchor="end">{xLabel}</text>
				<text x={box.l} y={box.t - 8} fontSize="11" fill={MUTED}>{yLabel}</text>
				{groups.map((g, i) => (
					<g key={`e${g.key}`} opacity={dim(i)}>
						<path d={path(ellipses[i])} fill={COLORS[i % 3]} fillOpacity="0.06" stroke={COLORS[i % 3]} strokeWidth="1.5" />
					</g>
				))}
				{groups.map((g, i) => g.points.map(([a, b], j) => (
					<circle key={`${g.key}-${j}`} cx={x(a)} cy={y(b)} r="3.5" fill={COLORS[i % 3]} stroke={PAPER} strokeWidth="1" opacity={0.8 * dim(i)}
						onMouseEnter={() => setHover({ i, cx: x(a), lines: [`One ${noun}, ${g.name}`, `${xLabel}: ${a}`, `${yLabel}: ${b}`] })} onMouseLeave={() => setHover(null)} />
				)))}
				{groups.map((g, i) => (
					<line key={`h${g.key}`} x1={x(grand[0])} y1={y(grand[1])} x2={x(g.mean[0])} y2={y(g.mean[1])} stroke={INK} strokeWidth="1" strokeDasharray="3 3" opacity={dim(i)} />
				))}
				{groups.map((g, i) => (
					<g key={`m${g.key}`} onMouseEnter={() => setHover({ i, cx: x(g.mean[0]), lines: [`${g.name}: the mean pair of ${g.n} ${noun}s`, `${xLabel}: ${fmt(g.mean[0])} (SD ${fmt(g.sd[0])})`,
						`${yLabel}: ${fmt(g.mean[1])} (SD ${fmt(g.sd[1])})`, `r = ${fmtR(g.r)} within the group`, `${g.inside} of ${g.n} inside the ${pct}% ellipse`] })} onMouseLeave={() => setHover(null)}>
						<circle cx={x(g.mean[0])} cy={y(g.mean[1])} r="12" fill="transparent" />
						<circle cx={x(g.mean[0])} cy={y(g.mean[1])} r="7" fill={COLORS[i % 3]} stroke={INK} strokeWidth="1.5" />
					</g>
				))}
				<g onMouseEnter={() => setHover({ cx: x(grand[0]), lines: [`Grand mean of all ${total} ${noun}s`, `${xLabel}: ${fmt(grand[0])}`, `${yLabel}: ${fmt(grand[1])}`] })} onMouseLeave={() => setHover(null)}>
					<circle cx={x(grand[0])} cy={y(grand[1])} r="10" fill="transparent" />
					<Cross cx={x(grand[0])} cy={y(grand[1])} />
				</g>
				{groups.map((g, i) => (
					<g key={`l${g.key}`}>
						<circle cx={box.r + 15} cy={labels[i].y - 10} r="5" fill={COLORS[i % 3]} stroke={INK} strokeWidth="1" />
						<text x={box.r + 25} y={labels[i].y - 6} fontSize="12" fill={INK} fontWeight="600">{g.name}</text>
						<text x={box.r + 25} y={labels[i].y + 8} fontSize="11" fill={MUTED}>{pair(g.mean)}</text>
					</g>
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each small dot is one {noun}; each large dot is {article(gl)}'s mean pair ({xl}, {yl}), its centroid, and the cross is the grand mean {pair(grand)}.
				H adds up how far the centroids sit from the cross (the dashed lines), once per {noun}.
				E adds up how far each {noun} sits from their own {gl}'s centroid, the spread the ellipses outline.
				Each ellipse would hold {pct}% of a group whose scores were bivariate normal with that {gl}'s means, SDs and correlation; here {groups.every((g) => g.n === groups[0].n) ? `${listing(groups.map((g) => g.inside))} of ${groups[0].n}` : listing(groups.map((g) => `${g.inside} of ${g.n}`))} fall inside.
				Their {tilt} tilt is the pooled within-group correlation, r = {fmtR(pooledR)}: within {article(gl)}, {noun}s higher on {xl} tend to be {pooledR < 0 ? "lower" : "higher"} on {yl}.
			</figcaption>
		</figure>
	);
}
